import type React from 'react'
import type { DefaultTheme } from 'styled-components'
import styled, { css } from 'styled-components'

interface AppBarProps extends React.HTMLAttributes<HTMLDivElement> {
  border?: 'none' | 'bottom' | 'top' | 'all'
  children: React.ReactNode
  noShadow?: boolean
  placement?: 'top' | 'bottom'
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative'
  tag?: 'header' | 'div' | 'footer'
}

type StylesProps = DollarPrefixed<Required<Pick<AppBarProps, 'border' | 'position' | 'noShadow' | 'placement'>>>

function getBorderStyles({ $border, theme }: StylesProps & { theme: DefaultTheme }) {
  switch ($border) {
    case 'bottom':
      return css`
        border-bottom: 1px solid ${theme.colors.common.paperStroke};
      `
    case 'top':
      return css`
        border-top: 1px solid ${theme.colors.common.paperStroke};
      `
    case 'all':
      return css`
        border: 1px solid ${theme.colors.common.paperStroke};
      `
    default:
      return ''
  }
}

function getPlacementStyles({ $placement, theme }: StylesProps & { theme: DefaultTheme }) {
  return css`
    ${$placement === 'top' ? `top: calc(var(--top, 0px) + ${theme.newVersionBarOffset})` : 'bottom: var(--bottom, 0)'};
  `
}

const StyledAppBar = styled.header<StylesProps>`
  width: 100%;
  left: 0;
  right: 0;
  flex-shrink: 0;
  position: ${({ $position }) => $position};
  background-color: ${({ theme }) => theme.colors.gray[0]};
  display: flex;
  z-index: ${({ theme }) => theme.zIndex.appBar};
  transition:
    box-shadow ease-in-out 400ms,
    top ease-in-out 400ms,
    bottom ease-in-out 400ms;
  box-sizing: border-box;
  flex-direction: column;
  padding-right: var(--floating-ui-scrollbar-width, 0);

  ${({ $noShadow }) =>
    !$noShadow &&
    css`
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);
    `};

  ${getBorderStyles}
  ${getPlacementStyles}
`

export const AppBar: React.FC<AppBarProps> = ({
  border = 'none',
  children,
  noShadow = false,
  placement = 'top',
  position = 'fixed',
  tag = 'header',
  ...rest
}) => (
  <StyledAppBar as={tag} $border={border} $noShadow={noShadow} $placement={placement} $position={position} {...rest}>
    {children}
  </StyledAppBar>
)
